import * as B from '@blueprintjs/core';
import * as geojson from 'geojson';
import React from 'react';

import {LegendUIProvider} from 'app/components/DeclarativeMap/LegendUIContext';
import DraggableMapOverlayDialog from 'app/components/MapOverlayDialog/DraggableMapOverlayDialog';

import {useGrid} from './GridContext';
import {GridFeatureCollection, cellIdToColRow, findNextExistingCell} from './gridGeometry';
import GridMiniMap from './GridMiniMap';
import {gridPanelLeft} from './panelLayout';
import cs from './styles.module.scss';
import {CellId, GridSpec} from './types';
import {MapInteractionTool} from '../view';

interface Props {
  isSidebarExpanded: boolean;
  activeMapInteractionTool: MapInteractionTool | null;
}

interface ActiveCellContentProps {
  activeCellId: CellId;
  spec: GridSpec | null;
  existingCellIds: Set<CellId> | null;
  featureGeometry: geojson.Geometry | null;
  featureCollection: GridFeatureCollection | null;
  reviewedCellIds: Set<CellId>;
  toggleCellReviewed: (id: CellId) => void;
  setActiveCellId: React.Dispatch<React.SetStateAction<CellId | null>>;
}

const ActiveCellContent: React.FunctionComponent<ActiveCellContentProps> = ({
  activeCellId,
  spec,
  existingCellIds,
  featureGeometry,
  featureCollection,
  reviewedCellIds,
  toggleCellReviewed,
  setActiveCellId,
}) => {
  const [col, row] = cellIdToColRow(activeCellId);
  const isReviewed = reviewedCellIds.has(activeCellId);
  const reviewedCount = reviewedCellIds.size;
  const totalCount = existingCellIds?.size ?? 0;
  const pct = totalCount > 0 ? Math.round((reviewedCount / totalCount) * 100) : 0;

  const navigate = (direction: 'left' | 'right') => {
    if (!spec || !existingCellIds) return;
    const next = findNextExistingCell(col, row, direction, spec.cols, spec.rows, existingCellIds);
    if (next) setActiveCellId(next);
  };
  const handleToggleAndNext = () => {
    toggleCellReviewed(activeCellId);
    // Only advance on the mark direction. Unmarking is usually a correction —
    // the user wants to stay on the cell to inspect it, not skip past.
    if (!isReviewed) navigate('right');
  };

  return (
    <div className={cs.reviewPanelContent}>
      <div className={cs.reviewPanelProgressSection}>
        {featureGeometry && featureCollection && (
          <div className={cs.reviewPanelMiniMapRow}>
            <GridMiniMap
              featureGeometry={featureGeometry}
              featureCollection={featureCollection}
              activeCellId={activeCellId}
              reviewedCellIds={reviewedCellIds}
              onCellClick={setActiveCellId}
              width={260}
              height={160}
            />
          </div>
        )}
        <div className={cs.reviewPanelProgressHeader}>
          <span className={cs.reviewPanelProgressCountLabel}>
            <strong className={cs.reviewPanelProgressActiveCount}>{reviewedCount}</strong>
            {' '}of {totalCount} cells
          </span>
          <span className={cs.reviewPanelProgressPct}>{pct}%</span>
        </div>
        <B.ProgressBar
          value={totalCount > 0 ? reviewedCount / totalCount : 0}
          animate={false}
          stripes={false}
          intent={B.Intent.SUCCESS}
        />
      </div>
      <div className={cs.reviewPanelNavButtons}>
        <B.Button
          fill
          text={isReviewed ? 'Unmark reviewed' : 'Mark reviewed & next'}
          intent={B.Intent.PRIMARY}
          onClick={handleToggleAndNext}
        />
        <div className={cs.reviewPanelNavRow}>
          <B.Button text="Previous" outlined onClick={() => navigate('left')} />
          <B.Button text="Skip" outlined onClick={() => navigate('right')} />
        </div>
      </div>
    </div>
  );
};

const GridReviewPanel: React.FunctionComponent<Props> = ({
  isSidebarExpanded,
  activeMapInteractionTool,
}) => {
  const {
    setIsEnabled,
    activeCellId,
    setActiveCellId,
    reviewedCellIds,
    toggleCellReviewed,
    resetGrid,
    spec,
    existingCellIds,
    featureGeometry,
    featureCollection,
  } = useGrid();

  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [isConfirmingReset, setIsConfirmingReset] = React.useState(false);

  const left = gridPanelLeft(isSidebarExpanded, activeMapInteractionTool);

  return (
    <LegendUIProvider isLegendCollapsed={isCollapsed} setIsLegendCollapsed={setIsCollapsed}>
      <DraggableMapOverlayDialog
        title="Grid review"
        initialPosition={{top: 10, left}}
        onClose={() => setIsEnabled(false)}
        additionalButtons={
          <>
            <B.Popover
              key="shortcuts"
              interactionKind={B.PopoverInteractionKind.HOVER}
              position={B.Position.BOTTOM}
              content={
                <div className={cs.shortcutsPopover}>
                  <h4 className={cs.shortcutsHeader}>Keyboard shortcuts</h4>
                  <div className={cs.shortcut}>
                    <div>Navigate</div>
                    <div className={cs.shortcutKeys}>
                      <kbd className={B.Classes.KEY}>
                        <B.Icon icon="arrow-left" />
                      </kbd>
                      <kbd className={B.Classes.KEY}>
                        <B.Icon icon="arrow-right" />
                      </kbd>
                      <kbd className={B.Classes.KEY}>
                        <B.Icon icon="arrow-up" />
                      </kbd>
                      <kbd className={B.Classes.KEY}>
                        <B.Icon icon="arrow-down" />
                      </kbd>
                    </div>
                  </div>
                  <div className={cs.shortcut}>
                    <div>Toggle review state</div>
                    <div className={cs.shortcutKeys}>
                      <kbd className={B.Classes.KEY}>r</kbd>
                    </div>
                  </div>
                </div>
              }
            >
              <B.Button icon="key-command" minimal />
            </B.Popover>
            <B.Tooltip content="Reset grid" key="reset">
              <B.Button icon="reset" minimal onClick={() => setIsConfirmingReset(true)} />
            </B.Tooltip>
          </>
        }
      >
        {activeCellId ? (
          <ActiveCellContent
            activeCellId={activeCellId}
            spec={spec}
            existingCellIds={existingCellIds}
            featureGeometry={featureGeometry}
            featureCollection={featureCollection}
            reviewedCellIds={reviewedCellIds}
            toggleCellReviewed={toggleCellReviewed}
            setActiveCellId={setActiveCellId}
          />
        ) : (
          <p>Click a grid cell to get started with your review.</p>
        )}
      </DraggableMapOverlayDialog>
      <B.Alert
        isOpen={isConfirmingReset}
        cancelButtonText="Cancel"
        confirmButtonText="Confirm"
        intent={B.Intent.PRIMARY}
        canOutsideClickCancel
        canEscapeKeyCancel
        icon="reset"
        onCancel={() => setIsConfirmingReset(false)}
        onConfirm={() => {
          resetGrid();
          setIsConfirmingReset(false);
        }}
      >
        Are you sure you want to reset this grid? This will clear all reviewed cells and can't be
        undone.
      </B.Alert>
    </LegendUIProvider>
  );
};

export default GridReviewPanel;
